import { AccountPortfolio, AccountTrade, AccountTransfer, universalAddressToSymbol } from "./";

// Helper types for better type safety
interface AssetPosition {
    symbol: string;
    totalAmount: number;
    totalCostBasis: number;
    averageCostPrice: number;
    realizedPnL: number;
    unrealizedPnL: number;
    currentValue: number;
    trades: number;
    buyTrades: number;
    sellTrades: number;
    totalBought: number;     // Total value bought
    totalSold: number;       // Total value sold
    totalCosts: number;      // Total transaction costs
    totalBuyAmount: number;  // Total amount/quantity bought
    totalSellAmount: number; // Total amount/quantity sold
    totalBuyValue: number;   // Total value of all purchases
    totalSellValue: number;  // Total value of all sales
    avgBuyPrice: number;     // Volume-weighted average buy price
    avgSellPrice: number;    // Volume-weighted average sell price
}

export interface MonthlyPnLData {
    month: string; // YYYY-MM-DD format for first day of month
    year: number;
    monthName: string; // "Jan", "Feb", etc.
    realizedPnL: number;
    tradeCount: number;
    buyCount: number;
    sellCount: number;
    volume: number;
    costs: number;
}

interface TradingAnalysis {
    // Overall metrics
    totalTrades: number;
    buyCount: number;
    sellCount: number;
    totalVolumeBought: number;
    totalVolumeSold: number;
    totalCosts: number;

    // P&L breakdown
    totalRealizedPnL: number;
    totalUnrealizedPnL: number;
    netPnL: number;

    // Asset breakdown
    assetPositions: Record<string, AssetPosition>;

    // Monthly data (includes all months in range, even with zero activity)
    monthlyData: Record<number, MonthlyPnLData[]>;

    // Summary stats
    avgTradeSize: number;
    avgBuyPrice: number;
    avgSellPrice: number;
    winRate: number; // percentage of profitable trades
    profitableTrades: number;
    unprofitableTrades: number;
}

export function calculateTradingAnalysis(
    trades: AccountTrade[],
    transfers: AccountTransfer[],
    firstPortfolio: AccountPortfolio | null,
    latestPortfolio: AccountPortfolio | null,
    currency: string,
    dateRange: { from: Date; to: Date },
    accountIds?: string[],
): TradingAnalysis {
    const { from: periodStart, to: periodEnd } = dateRange;

    // Generate all months in the period
    const allMonths: MonthlyPnLData[] = [];
    const current = new Date(periodStart.getFullYear(), periodStart.getMonth(), 1);
    const end = new Date(periodEnd.getFullYear(), periodEnd.getMonth(), 1);

    while (current <= end) {
        const monthKey = `${current.getFullYear()}-${String(current.getMonth() + 1).padStart(2, '0')}-01`;
        allMonths.push({
            month: monthKey,
            year: current.getFullYear(),
            monthName: current.toLocaleDateString('en-US', { month: 'short' }),
            realizedPnL: 0,
            tradeCount: 0,
            buyCount: 0,
            sellCount: 0,
            volume: 0,
            costs: 0
        });
        current.setMonth(current.getMonth() + 1);
    }

    // Track lifetime positions for accurate cost basis
    const positions: Record<string, {
        totalAmount: number;
        totalCostBasis: number;
        averageCostPrice: number;
        realizedPnL: number;
        totalCosts: number;
        trades: number;
        buyTrades: number;
        sellTrades: number;
        totalBought: number;
        totalSold: number;
        totalBuyAmount: number;
        totalSellAmount: number;
        totalBuyValue: number;
        totalSellValue: number;
    }> = {};

    // Get unique base assets from trades
    const quoteAssets = new Set(trades.map(trade => trade.trade.quote_asset));

    // Initialize positions from first portfolio
    if (firstPortfolio?.asset_data?.balances) {
        for (const [assetId, balance] of Object.entries(firstPortfolio.asset_data.balances)) {
            if (!quoteAssets.has(assetId)) {
                const amount = balance.amount || 0;
                const value = balance.valuation?.global?.[currency] || 0;

                if (amount > 0) {
                    positions[assetId] = {
                        totalAmount: amount,
                        totalCostBasis: value,
                        averageCostPrice: amount > 0 ? value / amount : 0,
                        realizedPnL: 0,
                        totalCosts: 0,
                        trades: 0,
                        buyTrades: 0,
                        sellTrades: 0,
                        totalBought: 0,
                        totalSold: 0,
                        totalBuyAmount: 0,
                        totalSellAmount: 0,
                        totalBuyValue: 0,
                        totalSellValue: 0,
                    };
                }
            }
        }
    }

    // Sort trades chronologically
    const sortedTrades = [...trades].sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());

    // Sort trades chronologically
    const sortedTransfers = [...transfers].sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());

    // Combine and sort ALL transactions chronologically
    const allTransactions = [
        ...sortedTrades.map(trade => ({ type: 'trade' as const, data: trade, timestamp: trade.timestamp })),
        ...sortedTransfers.map(transfer => ({ type: 'transfer' as const, data: transfer, timestamp: transfer.timestamp }))
    ].sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());

    // Counters
    let totalTrades = 0;
    let buyCount = 0;
    let sellCount = 0;
    let totalVolumeBought = 0;
    let totalVolumeSold = 0;
    let totalCosts = 0;
    let totalBuyAmountAcrossAssets = 0;
    let totalSellAmountAcrossAssets = 0;

    // Process ALL transactions in chronological order
    for (const transaction of allTransactions) {
        if (transaction.type === 'trade') {
            const trade = transaction.data;
            const baseAsset = trade.trade.base_asset;
            const isBuy = trade.trade.side === 'buy';
            const amount = trade.trade.base_amount;
            const value = trade.trade.base_valuation?.global?.[currency] || 0;
            const cost = trade.cost_valuation?.global?.[currency] || 0;
            const tradeDate = trade.timestamp;
            const monthKey = `${tradeDate.getFullYear()}-${String(tradeDate.getMonth() + 1).padStart(2, '0')}-01`;

            // Initialize position if doesn't exist
            if (!positions[baseAsset]) {
                positions[baseAsset] = {
                    totalAmount: 0,
                    totalCostBasis: 0,
                    averageCostPrice: 0,
                    realizedPnL: 0,
                    totalCosts: 0,
                    trades: 0,
                    buyTrades: 0,
                    sellTrades: 0,
                    totalBought: 0,
                    totalSold: 0,
                    totalBuyAmount: 0,
                    totalSellAmount: 0,
                    totalBuyValue: 0,
                    totalSellValue: 0,
                };
            }

            const position = positions[baseAsset];
            const monthData = allMonths.find(m => m.month === monthKey);

            position.trades += 1;
            position.totalCosts += cost;
            totalTrades += 1;
            totalCosts += cost;

            if (monthData) {
                monthData.tradeCount += 1;
                monthData.volume += value;
                monthData.costs += cost;
            }

            if (isBuy) {
                // Update average cost basis
                const newTotalAmount = position.totalAmount + amount;
                const newTotalCostBasis = position.totalCostBasis + value;

                position.totalAmount = newTotalAmount;
                position.totalCostBasis = newTotalCostBasis;
                position.averageCostPrice = newTotalAmount > 0 ? newTotalCostBasis / newTotalAmount : 0;
                position.buyTrades += 1;
                position.totalBought += value;
                position.totalBuyAmount += amount;
                position.totalBuyValue += value;

                buyCount += 1;
                totalVolumeBought += value;
                totalBuyAmountAcrossAssets += amount;

                if (monthData) {
                    monthData.buyCount += 1;
                }
            } else {
                // Calculate realized P&L using current average cost basis
                const avgCostOfSold = position.averageCostPrice * amount;
                const realizedPnLFromSell = value - avgCostOfSold;

                // Update position
                position.totalAmount -= amount;
                position.totalCostBasis -= avgCostOfSold;
                position.realizedPnL += realizedPnLFromSell;

                position.sellTrades += 1;
                position.totalSold += value;
                position.totalSellAmount += amount;
                position.totalSellValue += value;

                sellCount += 1;
                totalVolumeSold += value;
                totalSellAmountAcrossAssets += amount;

                // Update monthly data with realized P&L
                if (monthData) {
                    monthData.realizedPnL += realizedPnLFromSell;
                    monthData.sellCount += 1;
                }
            }
        } else {
            const transfer = transaction.data;
            const asset = transfer.asset;
            const amount = transfer.amount;
            const value = transfer.valuation?.global?.[currency] || 0;
            const cost = transfer.cost_valuation?.global?.[currency] || 0;
            const transferDate = transfer.timestamp;
            const matchIds = accountIds ?? (latestPortfolio?.account_id ? [latestPortfolio.account_id] : []);
            const isDeposit = matchIds.includes(transfer.target);
            const isWithdrawal = matchIds.includes(transfer.source);

            // Skip if it's not a deposit or withdrawal for this account
            if (!isDeposit && !isWithdrawal) continue;

            // Initialize position if doesn't exist
            if (!positions[asset]) {
                positions[asset] = {
                    totalAmount: 0,
                    totalCostBasis: 0,
                    averageCostPrice: 0,
                    realizedPnL: 0,
                    totalCosts: 0,
                    trades: 0,
                    buyTrades: 0,
                    sellTrades: 0,
                    totalBought: 0,
                    totalSold: 0,
                    totalBuyAmount: 0,
                    totalSellAmount: 0,
                    totalBuyValue: 0,
                    totalSellValue: 0,
                };
            }

            const position = positions[asset];
            position.totalCosts += cost;
            totalCosts += cost;

            if (isDeposit) {
                // Deposits DO have cost basis = market value at time of deposit
                const newTotalAmount = position.totalAmount + amount;
                const newTotalCostBasis = position.totalCostBasis + value;
                position.totalAmount = newTotalAmount;
                position.totalCostBasis = newTotalCostBasis;
                position.averageCostPrice = newTotalAmount > 0 ? newTotalCostBasis / newTotalAmount : 0;
            } else if (isWithdrawal) {
                const avgCostOfWithdrawn = position.averageCostPrice * amount;
                position.totalAmount -= amount;
                position.totalCostBasis -= avgCostOfWithdrawn;
                // Withdrawals don't generate realized P&L - you're just moving assets
            }
        }
    }

    // Calculate unrealized P&L using latest portfolio
    let totalUnrealizedPnL = 0;
    const assetPositions: Record<string, AssetPosition> = {};

    for (const [assetId, position] of Object.entries(positions)) {
        if (!quoteAssets.has(assetId) && position.trades > 0) {
            const currentValue = latestPortfolio?.asset_data?.balances?.[assetId]?.valuation?.global?.[currency] || 0;
            const unrealizedPnL = currentValue - position.totalCostBasis;
            totalUnrealizedPnL += unrealizedPnL;

            const symbol = latestPortfolio?.asset_data?.balances?.[assetId]?.symbol ||
                firstPortfolio?.asset_data?.balances?.[assetId]?.symbol ||
                universalAddressToSymbol?.(assetId) ||
                assetId;

            if (unrealizedPnL > 1 || position.trades > 0) {
                assetPositions[assetId] = {
                    symbol,
                    totalAmount: position.totalAmount,
                    totalCostBasis: position.totalCostBasis,
                    averageCostPrice: position.averageCostPrice,
                    realizedPnL: position.realizedPnL,
                    unrealizedPnL,
                    currentValue,
                    trades: position.trades,
                    buyTrades: position.buyTrades,
                    sellTrades: position.sellTrades,
                    totalBought: position.totalBought,
                    totalSold: position.totalSold,
                    totalCosts: position.totalCosts,
                    totalBuyAmount: position.totalBuyAmount,
                    totalSellAmount: position.totalSellAmount,
                    totalBuyValue: position.totalBuyValue,
                    totalSellValue: position.totalSellValue,
                    avgBuyPrice: position.totalBuyAmount > 0 ? position.totalBuyValue / position.totalBuyAmount : 0,
                    avgSellPrice: position.totalSellAmount > 0 ? position.totalSellValue / position.totalSellAmount : 0
                };
            }
        }
    }

    const totalRealizedPnL = Object.values(positions).reduce((sum, pos) => sum + pos.realizedPnL, 0);

    // Win rate based on profitable vs unprofitable assets
    const profitableAssets = Object.values(positions).filter(pos => pos.realizedPnL > 0).length;
    const unprofitableAssets = Object.values(positions).filter(pos => pos.realizedPnL < 0).length;
    const winRate = (profitableAssets + unprofitableAssets) > 0 ? (profitableAssets / (profitableAssets + unprofitableAssets)) * 100 : 0;


    // trim leading 0 months
    const firstActiveMonthIndex = allMonths.findIndex(
        m => m.tradeCount > 0 || m.realizedPnL !== 0
    );
    const trimmedMonths = firstActiveMonthIndex !== -1
        ? allMonths.slice(firstActiveMonthIndex)
        : [];

    const monthlyData = trimmedMonths.reduce((acc, item) => {
        if (!acc[item.year]) acc[item.year] = [];
        acc[item.year].push(item);
        return acc;
    }, {} as Record<number, MonthlyPnLData[]>);

    return {
        totalTrades,
        buyCount,
        sellCount,
        totalVolumeBought,
        totalVolumeSold,
        totalCosts,
        totalRealizedPnL,
        totalUnrealizedPnL,
        netPnL: totalRealizedPnL + totalUnrealizedPnL,
        assetPositions,
        monthlyData: monthlyData,
        avgTradeSize: totalTrades > 0 ? (totalVolumeBought + totalVolumeSold) / (2 * totalTrades) : 0,
        avgBuyPrice: totalBuyAmountAcrossAssets > 0 ? totalVolumeBought / totalBuyAmountAcrossAssets : 0,
        avgSellPrice: totalSellAmountAcrossAssets > 0 ? totalVolumeSold / totalSellAmountAcrossAssets : 0,
        winRate,
        profitableTrades: profitableAssets,
        unprofitableTrades: unprofitableAssets
    };
}